import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  groupMenuOptionRows,
  parseModifierKind,
  type MenuOptionGroup,
  type MenuOptionChoice,
} from '../domain/menuModifiers';

type FlatRow = {
  product_id: string;
  group_id: string;
  group_name: string;
  selection_min: number;
  selection_max: number;
  group_sort: number;
  choice_id: string;
  choice_name: string;
  price_delta: number;
  max_qty: number;
  linked_product_id?: string | null;
  is_available: boolean;
  choice_sort: number;
  modifier_kind?: string | null;
  allergen_tags?: string[] | null;
  kitchen_label?: string | null;
  substitutes_choice_id?: string | null;
  substitutes_product_id?: string | null;
};

/** In-memory cache for fast POS taps. */
const MENU_OPTIONS_TTL_MS = 5 * 60 * 1000;
/** Disk cache for offline personalization (fail-closed if missing). */
const MENU_OPTIONS_DISK_TTL_MS = 24 * 60 * 60 * 1000;
const DISK_KEY_PREFIX = 'menu_options_v1:';
const menuOptionsCache = new Map<string, { at: number; groups: MenuOptionGroup[] }>();

export function invalidateMenuOptionsCache(productId?: string): void {
  if (!productId) {
    menuOptionsCache.clear();
    void AsyncStorage.getAllKeys().then((keys) => {
      const doomed = keys.filter((k) => k.startsWith(DISK_KEY_PREFIX));
      if (doomed.length) void AsyncStorage.multiRemove(doomed);
    });
    return;
  }
  menuOptionsCache.delete(productId);
  void AsyncStorage.removeItem(`${DISK_KEY_PREFIX}${productId}`);
}

/** Solo RAM. Al entrar al POS: el próximo tap vuelve a pedir extras, sin borrar el fallback offline. */
export function invalidateMenuOptionsMemoryCache(): void {
  menuOptionsCache.clear();
}

/** Sync peek for POS: avoid opening the modal when we already know there are no groups. */
export function peekCachedMenuOptions(productId: string): MenuOptionGroup[] | null {
  const hit = menuOptionsCache.get(productId);
  if (!hit) return null;
  if (Date.now() - hit.at >= MENU_OPTIONS_TTL_MS) return null;
  return hit.groups;
}

async function persistMenuOptions(productId: string, groups: MenuOptionGroup[]): Promise<void> {
  try {
    await AsyncStorage.setItem(
      `${DISK_KEY_PREFIX}${productId}`,
      JSON.stringify({ at: Date.now(), groups }),
    );
  } catch {
    // ignore quota / web private mode
  }
}

/** Offline fallback: null = unknown, [] = known without options, nonempty = groups. */
export async function readPersistedMenuOptions(
  productId: string,
): Promise<MenuOptionGroup[] | null> {
  try {
    const raw = await AsyncStorage.getItem(`${DISK_KEY_PREFIX}${productId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: number; groups?: MenuOptionGroup[] };
    if (!parsed || typeof parsed.at !== 'number' || !Array.isArray(parsed.groups)) return null;
    if (Date.now() - parsed.at >= MENU_OPTIONS_DISK_TTL_MS) return null;
    menuOptionsCache.set(productId, { at: Date.now(), groups: parsed.groups });
    return parsed.groups;
  } catch {
    return null;
  }
}

export async function fetchMenuOptionsForProducts(
  productIds: string[],
): Promise<Map<string, MenuOptionGroup[]>> {
  if (productIds.length === 0) return new Map();
  const now = Date.now();
  const result = new Map<string, MenuOptionGroup[]>();
  const missing: string[] = [];
  for (const id of productIds) {
    const hit = menuOptionsCache.get(id);
    if (hit && now - hit.at < MENU_OPTIONS_TTL_MS) {
      result.set(id, hit.groups);
    } else {
      missing.push(id);
    }
  }
  if (missing.length === 0) return result;

  try {
    const { data, error } = await supabase.rpc('list_menu_options_for_products', {
      p_product_ids: missing,
    });
    if (error) throw new Error(error.message);
    const rows = (Array.isArray(data) ? data : []) as FlatRow[];
    const fresh = groupMenuOptionRows(rows);
    for (const id of missing) {
      const groups = fresh.get(id) ?? [];
      menuOptionsCache.set(id, { at: now, groups });
      void persistMenuOptions(id, groups);
      result.set(id, groups);
    }
    return result;
  } catch (err) {
    // Offline: hydrate from disk; leave missing out of map so caller can fail-closed.
    for (const id of missing) {
      const disk = await readPersistedMenuOptions(id);
      if (disk != null) result.set(id, disk);
    }
    if (result.size === 0 && missing.length > 0) throw err;
    return result;
  }
}

export type OrgOptionGroup = MenuOptionGroup & {
  isActive: boolean;
  productIds: string[];
};

export async function listOrgOptionGroups(): Promise<OrgOptionGroup[]> {
  const { data, error } = await supabase.rpc('list_org_option_groups');
  if (error) throw new Error(error.message);
  const raw = (Array.isArray(data) ? data : []) as Array<{
    id: string;
    name: string;
    selection_min: number;
    selection_max: number;
    sort_order: number;
    is_active: boolean;
    choices: Array<{
      id: string;
      name: string;
      price_delta: number;
      max_qty: number;
      linked_product_id?: string | null;
      is_available: boolean;
      sort_order: number;
      modifier_kind?: string | null;
      allergen_tags?: string[] | null;
      kitchen_label?: string | null;
      substitutes_choice_id?: string | null;
      substitutes_product_id?: string | null;
    }>;
    product_ids: string[];
  }>;
  return raw.map((g) => ({
    id: g.id,
    name: g.name,
    selectionMin: g.selection_min,
    selectionMax: g.selection_max,
    sortOrder: g.sort_order,
    isActive: g.is_active,
    productIds: Array.isArray(g.product_ids) ? g.product_ids : [],
    choices: (g.choices ?? []).map(
      (c): MenuOptionChoice => ({
        id: c.id,
        name: c.name,
        priceDelta: Number(c.price_delta) || 0,
        maxQty: c.max_qty,
        linkedProductId: c.linked_product_id ?? null,
        isAvailable: c.is_available !== false,
        sortOrder: c.sort_order,
        modifierKind: parseModifierKind(c.modifier_kind),
        allergenTags: Array.isArray(c.allergen_tags) ? c.allergen_tags.filter(Boolean) : [],
        kitchenLabel: c.kitchen_label ?? null,
        substitutesChoiceId: c.substitutes_choice_id ?? null,
        substitutesProductId: c.substitutes_product_id ?? null,
      }),
    ),
  }));
}

export async function upsertOptionGroup(input: {
  name: string;
  selectionMin: number;
  selectionMax: number;
  sortOrder?: number;
  groupId?: string | null;
  choices: Array<{
    id?: string;
    name: string;
    priceDelta: number;
    maxQty?: number;
    linkedProductId?: string | null;
    isAvailable?: boolean;
    sortOrder?: number;
    modifierKind?: string | null;
    allergenTags?: string[];
    kitchenLabel?: string | null;
    substitutesChoiceId?: string | null;
    substitutesProductId?: string | null;
  }>;
  productIds?: string[] | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_product_option_group', {
    p_name: input.name,
    p_selection_min: input.selectionMin,
    p_selection_max: input.selectionMax,
    p_sort_order: input.sortOrder ?? 0,
    p_group_id: input.groupId ?? null,
    p_choices: input.choices.map((c, i) => ({
      id: c.id,
      name: c.name,
      price_delta: c.priceDelta,
      max_qty: c.maxQty ?? 1,
      linked_product_id: c.linkedProductId ?? null,
      is_available: c.isAvailable !== false,
      sort_order: c.sortOrder ?? i,
      modifier_kind: c.modifierKind ?? null,
      allergen_tags: c.allergenTags ?? [],
      kitchen_label: c.kitchenLabel ?? null,
      substitutes_choice_id: c.substitutesChoiceId ?? null,
      substitutes_product_id: c.substitutesProductId ?? null,
    })),
    p_product_ids: input.productIds ?? null,
  });
  if (error) throw new Error(error.message);
  const id = (data as { id?: string } | null)?.id;
  if (!id) throw new Error('No se recibió id del grupo');
  invalidateMenuOptionsCache();
  return String(id);
}

export async function assignOptionGroupsToProduct(
  productId: string,
  groupIds: string[],
): Promise<void> {
  const { error } = await supabase.rpc('assign_option_groups_to_product', {
    p_product_id: productId,
    p_group_ids: groupIds,
  });
  if (error) throw new Error(error.message);
  invalidateMenuOptionsCache(productId);
}

export async function setOptionGroupActive(groupId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_product_option_group_active', {
    p_group_id: groupId,
    p_is_active: isActive,
  });
  if (error) throw new Error(error.message);
  invalidateMenuOptionsCache();
}

export async function setProductMenuAvailable(
  productId: string,
  menuAvailable: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('set_product_menu_available', {
    p_product_id: productId,
    p_menu_available: menuAvailable,
  });
  if (error) throw new Error(error.message);
  // ADR-017 Fase 4: SQLite eliminado — el servidor es source of truth.
  invalidateMenuOptionsCache(productId);
}
